import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

/**
 * Truncated text with a clickable "..." that opens a dialog of the full content.
 *
 * - Char mode (default): truncates to `maxLength` characters and appends "...".
 * - Line mode (`maxLines`): CSS line-clamps the text; clicking opens the dialog
 *   when the content is long enough to likely be clamped.
 */
export default function ExpandableText({
  text,
  maxLength = 15,
  maxLines,
  title = "Full text",
  emptyLabel = null,
  sx,
}) {
  const [open, setOpen] = useState(false);

  if (text == null || text === "") {
    return emptyLabel;
  }

  const value = String(text);
  const isLineMode = Boolean(maxLines);
  const isTruncated = isLineMode
    ? value.length > 80 || value.split("\n").length > maxLines
    : value.length > maxLength;

  const openDialog = (event) => {
    event?.stopPropagation?.();
    setOpen(true);
  };

  const dialog = (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      onClick={(e) => e.stopPropagation()}
      sx={{
        "& .MuiDialog-container .MuiPaper-elevation": {
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography
          sx={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "14px",
            color: "#333",
          }}
        >
          {value}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setOpen(false)}
          sx={{
            backgroundColor: "#142C5C",
            color: "#fff",
            textTransform: "none",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "#0f2247" },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (isLineMode) {
    return (
      <>
        <Typography
          component="span"
          onClick={isTruncated ? openDialog : undefined}
          sx={{
            fontSize: "inherit",
            color: "inherit",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            display: "-webkit-box",
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            cursor: isTruncated ? "pointer" : "default",
            "&:hover": isTruncated
              ? { color: "#142C5C", textDecoration: "underline" }
              : undefined,
            ...sx,
          }}
          title={isTruncated ? "Click to read full text" : undefined}
        >
          {value}
        </Typography>
        {dialog}
      </>
    );
  }

  if (!isTruncated) {
    return (
      <Typography
        component="span"
        sx={{ fontSize: "inherit", color: "inherit", ...sx }}
      >
        {value}
      </Typography>
    );
  }

  return (
    <>
      <Box
        component="span"
        sx={{
          fontSize: "inherit",
          color: "inherit",
          wordBreak: "break-word",
          ...sx,
        }}
      >
        {value.slice(0, maxLength)}
        <Box
          component="button"
          type="button"
          onClick={openDialog}
          title="Click to read full text"
          sx={{
            all: "unset",
            cursor: "pointer",
            color: "#142C5C",
            fontWeight: 600,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          ...
        </Box>
      </Box>
      {dialog}
    </>
  );
}
